import { useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useFaqs,
  useAddFaq,
  useUpdateFaq,
  useDeleteFaq,
  type StockFaq,
} from "@/api/stockSubresources";

interface Props {
  sku: string;
}

export function FaqsTab({ sku }: Props) {
  const { data: rows = [], isLoading } = useFaqs(sku);
  const addOne = useAddFaq(sku);
  const updOne = useUpdateFaq(sku);
  const delOne = useDeleteFaq(sku);

  const [showNew, setShowNew] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");

  async function onAdd() {
    const q = newQ.trim();
    const a = newA.trim();
    if (!q || !a) {
      toast.error("Frage und Antwort sind Pflicht");
      return;
    }
    try {
      await addOne.mutateAsync({ question: q, answer: a, position: rows.length });
      setNewQ("");
      setNewA("");
      setShowNew(false);
      toast.success("FAQ hinzugefuegt");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    }
  }

  async function onDelete(id: number) {
    try {
      await delOne.mutateAsync(id);
      toast.success("FAQ geloescht");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Loeschen fehlgeschlagen");
    }
  }

  return (
    <Panel
      title="FAQs"
      subtitle="Haeufige Fragen zum Artikel"
      actions={
        !showNew ? (
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="size-3.5" />
            FAQ hinzufuegen
          </Button>
        ) : undefined
      }
    >
      {showNew && (
        <div className="border border-primary/40 rounded-md p-4 mb-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
              Frage
            </div>
            <Input
              value={newQ}
              onChange={(e) => setNewQ(e.target.value)}
              placeholder="z.B. Wie lange ist die Garantie?"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
              Antwort
            </div>
            <Textarea
              value={newA}
              onChange={(e) => setNewA(e.target.value)}
              rows={3}
              placeholder="Freie Antwort"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setShowNew(false); setNewQ(""); setNewA(""); }}>
              Abbrechen
            </Button>
            <Button size="sm" onClick={onAdd} disabled={addOne.isPending}>
              <Save className="size-3.5" />
              Speichern
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted">Lade FAQs…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-sm text-muted py-10">
          Noch keine FAQs hinterlegt.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((f, i) => (
            <FaqCard
              key={f.id}
              faq={f}
              index={i}
              onSave={(payload) => updOne.mutateAsync({ id: f.id, payload })}
              onDelete={() => onDelete(f.id)}
              isSaving={updOne.isPending}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

function FaqCard({
  faq,
  index,
  onSave,
  onDelete,
  isSaving,
}: {
  faq: StockFaq;
  index: number;
  onSave: (payload: { question: string; answer: string }) => Promise<unknown>;
  onDelete: () => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(faq.question);
  const [a, setA] = useState(faq.answer);
  const dirty = q !== faq.question || a !== faq.answer;

  async function commit() {
    try {
      await onSave({ question: q, answer: a });
      toast.success("FAQ gespeichert");
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    }
  }

  return (
    <div className="border border-line rounded-md p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
            Frage {index + 1}
          </div>
          {editing ? (
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" />
          ) : (
            <div className="font-semibold text-sm mb-2">{faq.question}</div>
          )}
          {editing ? (
            <Textarea value={a} onChange={(e) => setA(e.target.value)} rows={3} />
          ) : (
            <div className="text-sm text-ink-2 leading-relaxed whitespace-pre-line">
              {faq.answer}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={commit}
                disabled={!dirty || isSaving}
                className="h-7 text-xs"
              >
                <Save className="size-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setEditing(false); setQ(faq.question); setA(faq.answer); }}
                className="h-7 text-xs"
              >
                <X className="size-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(true)}
                className="h-7 text-xs"
              >
                Bearbeiten
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDelete}
                className="h-7 text-xs text-coral hover:text-coral"
              >
                <Trash2 className="size-3" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
